import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { formatDate, formatRelativeTime } from '@utils/format';
import { ActiveBadge } from '@features/tags/components/cells/ActiveBadge';
import { ForecastRowActions } from './components/cells/ForecastRowActions';
import { ForecastSeriesCell } from './components/cells/ForecastSeriesCell';
import { HorizonChip } from './components/cells/HorizonChip';
import { MetricBadge } from './components/cells/MetricBadge';
import { PredictedRangeCell } from './components/cells/PredictedRangeCell';
import type { Forecast } from './schema';

export interface ForecastRowActionHandlers {
  onEdit: (row: Forecast) => void;
  onChart: (row: Forecast) => void;
}

export function buildForecastsColumns(
  actions: ForecastRowActionHandlers,
): ColDef<Forecast>[] {
  return [
    {
      colId: 'series',
      headerName: 'Series',
      flex: 1.5,
      minWidth: 220,
      sortable: true,
      pinned: 'left',
      lockPinned: true,
      cellRenderer: ForecastSeriesCell,
      valueGetter: (p) => p.data?.cropName ?? '',
    },
    {
      colId: 'metric',
      field: 'metric',
      headerName: 'Metric',
      width: 110,
      sortable: true,
      cellRenderer: MetricBadge,
    },
    {
      colId: 'horizon',
      field: 'horizonDays',
      headerName: 'Horizon',
      width: 110,
      sortable: true,
      cellRenderer: HorizonChip,
    },
    {
      colId: 'predictionDay',
      field: 'predictionDay',
      headerName: 'Predicted on',
      width: 150,
      sortable: true,
      cellRenderer: (p: ICellRendererParams<Forecast>) =>
        p.data ? (
          <span className="text-[13px] tabular-nums text-foreground">
            {formatDate(p.data.predictionDay)}
          </span>
        ) : null,
    },
    {
      colId: 'targetDate',
      field: 'targetDate',
      headerName: 'Target date',
      width: 150,
      sortable: true,
      cellRenderer: (p: ICellRendererParams<Forecast>) =>
        p.data ? (
          <span className="text-[13px] tabular-nums text-foreground">
            {formatDate(p.data.targetDate)}
          </span>
        ) : null,
    },
    {
      headerName: 'Predicted range',
      flex: 1,
      minWidth: 180,
      cellRenderer: PredictedRangeCell,
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 110,
      cellRenderer: ActiveBadge,
    },
    {
      colId: 'updated',
      field: 'updatedAt',
      headerName: 'Updated',
      width: 200,
      sortable: true,
      cellRenderer: (p: ICellRendererParams<Forecast>) =>
        p.data ? (
          <div className="flex flex-col leading-tight">
            <span className="text-[13px] font-semibold text-foreground">
              {formatDate(p.data.updatedAt)}
            </span>
            <span className="mt-0.5 text-[11px] text-foreground-muted">
              {formatRelativeTime(p.data.updatedAt)}
            </span>
          </div>
        ) : null,
    },
    {
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (p: ICellRendererParams<Forecast>) =>
        p.data ? <ForecastRowActions row={p.data} {...actions} /> : null,
    },
  ];
}
